// Batch Lifecycle Demo v2 — process model. Pure: no DOM, no timers, seeded.
// Spec: xl-specs/specs/2026-10-14-batch-lifecycle-demo-v2-design.md

export const CONSTANTS = Object.freeze({
  DT: 0.1,
  Q_FAST: 1.8, Q_DRIB: 0.35, Q_DRAIN: 6.0, PUMP_CAP: 18.0, LINE_DRAW: 1.25,
  TANK_CAP: 1200, TANK_PERMISSIVE_PCT: 72, TANK_HIHI_PCT: 90, TANK_LOLO_PCT: 5,
  MIX_HOLD_S: 90, SETTLE_S: 6, TARE_S: 8, HEEL_KG: 0.3,
  TOL_FRAC: 0.005, TOL_MIN_KG: 0.1,
  VALVE_T_OPEN: 0.5, VALVE_T_CLOSE: 0.5, OUTLET_T_OPEN: 1.0, OUTLET_T_CLOSE: 1.0,
  INFLIGHT_DELAY_S: 0.8, LC_TAU_S: 0.6, LC_NOISE_KG: 0.02,
  RPM_SLEW_UP: 60, RPM_SLEW_DOWN: 40,
  PREACT_GAIN: 0.6, PREACT_MIN_KG: 0.0, PREACT_MAX_KG: 1.2, PREACT_MAX_STEP: 0.25,
  WEAR_LIFE_STROKES: 11000, WEAR_FACTOR_AT_LIFE: 8.5, WEAR_KNEE_S_PER_STROKE: 0.04, WEAR_DUE_BATCHES: 40,
  SHOCK_LIMIT_G: 3.2,
  ALARM_RAISE_S: 1.0, ALARM_CLEAR_S: 3.0,
  BACKFILL_S: 8 * 3600,
})

/** Mulberry32: tiny seeded PRNG, good enough for noise. */
export function createRng(seed) {
  let a = seed >>> 0
  return function rng() {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Approximate N(0,1) from the rng (sum of 6 uniforms, centered). */
export function gauss(rng) {
  let s = 0
  for (let i = 0; i < 6; i++) s += rng()
  return (s - 3) * Math.sqrt(2)
}

/** Solenoid-style valve with linear stroke; tClose grows with wear (wearCloseTime). */
export function createValve({ tOpen, tClose, Q }) {
  const v = {
    pos: 0, cmd: 0, strokes: 0, Q,
    tOpen, tClose, tCloseNew: tClose,
    set(cmd) {
      cmd = cmd ? 1 : 0
      if (cmd !== v.cmd) {
        if (cmd === 0) v.strokes += 1
        v.cmd = cmd
      }
    },
    step(dt) {
      const rate = v.cmd > v.pos ? dt / v.tOpen : dt / v.tClose
      const d = v.cmd - v.pos
      v.pos += Math.sign(d) * Math.min(Math.abs(d), rate)
      if (v.pos < 1e-9) v.pos = 0
      if (v.pos > 1 - 1e-9) v.pos = 1
      return v.Q * v.pos
    },
    state() {
      if (v.pos <= 0) return "closed"
      if (v.pos >= 1) return "open"
      return "moving"
    },
  }
  return v
}

/** First-order load cell: meas lags true mass; seeded noise; optional zero offset (fault). */
export function createLoadCell({ tau, noiseSigma, rng }) {
  const lc = {
    true: 0, meas: 0, zeroOffset: 0, settled: 0,
    add(kg) { lc.true += kg },
    remove(kg) { const r = Math.min(lc.true, kg); lc.true -= r; return r },
    step(dt) {
      lc.settled += (lc.true - lc.settled) * (dt / tau)
      lc.meas = lc.settled + lc.zeroOffset + (noiseSigma ? noiseSigma * gauss(rng) : 0)
    },
  }
  return lc
}

/** Transport delay ring: material pushed now lands delay seconds later. */
export function createInflight({ delay, dt }) {
  const n = Math.max(1, Math.round(delay / dt))
  const ring = new Float64Array(n)
  let i = 0
  return {
    push(kg) { ring[i] = kg },
    pop() { i = (i + 1) % n; const out = ring[i]; ring[i] = 0; return out },
    total() { let s = 0; for (let k = 0; k < n; k++) s += ring[k]; return s },
  }
}

/** Motor with slew-limited rpm. */
export function createAgitator({ slewUp, slewDown }) {
  const m = {
    rpm: 0, target: 0,
    step(dt) {
      const d = m.target - m.rpm
      const lim = (d > 0 ? slewUp : slewDown) * dt
      m.rpm += Math.sign(d) * Math.min(Math.abs(d), lim)
      if (Math.abs(m.rpm) < 1e-9) m.rpm = 0
    },
  }
  return m
}

export const PHASES = ["IDLE", "TARE", "FAST", "DRIBBLE", "SETTLE", "VERIFY",
  "MIX_RAMP", "MIX_HOLD", "MIX_COAST", "WAIT_TRANSFER", "DRAIN", "EMPTY"]

export const STATION_DEFS = [
  { id: "ST-101", num: 101, name: "Filtered Water",          color: "#0ea5e9", dribbleBand: 6.0, preact: 0.58 },
  { id: "ST-102", num: 102, name: "Tomato Base Slurry",      color: "#ef4444", dribbleBand: 5.0, preact: 0.58 },
  { id: "ST-103", num: 103, name: "Emulsified Oil Blend",    color: "#eab308", dribbleBand: 4.5, preact: 0.58 },
  { id: "ST-104", num: 104, name: "Liquid Sweetener",        color: "#f97316", dribbleBand: 4.5, preact: 0.58 },
  { id: "ST-105", num: 105, name: "Acidulant & Spice",       color: "#a855f7", dribbleBand: 4.5, preact: 0.58 },
  { id: "ST-106", num: 106, name: "Hydrocolloid Stabilizer", color: "#10b981", dribbleBand: 4.5, preact: 0.58 },
]

export const RECIPES = {
  marinara: { name: "Classic Marinara Emulsion", targets: [120.0, 85.0, 42.0, 28.5, 16.0, 8.5], rpms: [950, 1150, 1420, 980, 1100, 1350] },
  bbq:      { name: "Smokehouse BBQ Glaze",      targets: [95.0, 92.0, 24.0, 44.0, 18.0, 7.0],  rpms: [900, 1100, 1300, 1050, 1150, 1280] },
  bechamel: { name: "High-Shear Culinary Béchamel", targets: [135.0, 78.0, 54.0, 22.0, 19.0, 12.0], rpms: [1050, 1250, 1480, 1000, 1180, 1420] },
}

const C = CONSTANTS

export function createUnit(def, { rng, emit }) {
  const u = {
    id: def.id, num: def.num, name: def.name, color: def.color,
    phase: "IDLE", phaseT: 0, batchId: 0,
    sp: 0, nextSp: null, rpmTarget: 0, nextRpm: null,
    dribbleBand: def.dribbleBand, preact: def.preact,
    cmdSrc: "Prog", hold: false,
    _pendingNext: null, _emptiedBatch: 0, _stickyBatch: null,
    xv01: createValve({ tOpen: C.VALVE_T_OPEN, tClose: C.VALVE_T_CLOSE, Q: C.Q_FAST }),
    xv02: createValve({ tOpen: C.VALVE_T_OPEN, tClose: C.VALVE_T_CLOSE, Q: C.Q_DRIB }),
    xv03: createValve({ tOpen: C.OUTLET_T_OPEN, tClose: C.OUTLET_T_CLOSE, Q: C.Q_DRAIN }),
    lc: createLoadCell({ tau: C.LC_TAU_S, noiseSigma: C.LC_NOISE_KG, rng }),
    inflight: createInflight({ delay: C.INFLIGHT_DELAY_S, dt: C.DT }),
    mtr: createAgitator({ slewUp: C.RPM_SLEW_UP, slewDown: C.RPM_SLEW_DOWN }),
    flowIn: 0, flowOut: 0, shockPk: 0.3, plcRms: 0.3, _plcRing: new Float64Array(10), _plcN: 0, _plcI: 0, _plcSum: 0,
    faults: new Set(),
    conditions: {
      tare_ok:        { kind: "permissive", label: "Load cell zero within band", ok: true, firstOut: false },
      tank_space:     { kind: "permissive", label: "TK-200 below 72 %",          ok: true, firstOut: false },
      shock_ok:       { kind: "interlock",  label: "Shock envelope below 3.2 g", ok: true, firstOut: false },
      tank_hihi:      { kind: "interlock",  label: "TK-200 high-high clear",     ok: true, firstOut: false },
    },
    wt() { return u.lc.meas },
    wtTrue() { return u.lc.true },
    setRecipe(targetKg, rpm) {
      if (u.phase === "IDLE" || u.phase === "TARE" || u.phase === "FAST") { u.sp = targetKg; u.rpmTarget = rpm; u.nextSp = null; u.nextRpm = null }
      else { u.nextSp = targetKg; u.nextRpm = rpm }
    },
    startBatch(batchId) {
      u.batchId = batchId
      if (u.nextSp != null) { u.sp = u.nextSp; u.rpmTarget = u.nextRpm; u.nextSp = null; u.nextRpm = null }
      go("TARE")
    },
    holdUnit() { u.hold = true; u.cmdSrc = "Oper"; u.xv01.set(0); u.xv02.set(0); u.xv03.set(0); u.mtr.target = 0 },
    resumeUnit() {
      u.hold = false; u.cmdSrc = "Prog"
      // holdUnit() shut every output; re-issue the current phase's.
      if (u.phase === "FAST") u.xv01.set(1)
      else if (u.phase === "DRIBBLE") u.xv02.set(1)
      else if (u.phase === "DRAIN") u.xv03.set(1)
      else if (u.phase === "MIX_RAMP" || u.phase === "MIX_HOLD") u.mtr.target = u.rpmTarget
    },
    step,
  }

  function go(next) {
    const from = u.phase
    u.phase = next; u.phaseT = 0
    emit("unit.phase", { unit: u.id, from, to: next, batchId: u.batchId })
  }
  function tol() { return Math.max(C.TOL_MIN_KG, C.TOL_FRAC * u.sp) }

  function step(dt, env) {
    // Devices always integrate, even on hold (valves finish their stroke).
    const fFast = u.xv01.step(dt)
    const fDrib = u.xv02.step(dt)
    u.flowIn = fFast + fDrib
    u.inflight.push(u.flowIn * dt)
    u.lc.add(u.inflight.pop())
    const wantOut = u.xv03.step(dt) * (env.drainScale ?? 1)
    u.flowOut = u.lc.remove(wantOut * dt) / dt
    u.lc.step(dt)
    u.mtr.step(dt)
    shock(dt, env)

    u.conditions.tank_space.ok = !!env.tankPermissive
    u.conditions.tank_hihi.ok = !env.tankInterlock
    if (env.tankInterlock && u.phase === "DRAIN") { u.xv03.set(0); u.conditions.tank_hihi.firstOut = true; go("WAIT_TRANSFER") }

    if (u.hold) return
    u.phaseT += dt   // the phase timer is frozen while held

    switch (u.phase) {
      case "IDLE": break
      case "TARE":
        if (u.phaseT >= C.TARE_S) {
          const ok = Math.abs(u.lc.meas) <= 0.3
          if (!ok && u.conditions.tare_ok.ok) u.conditions.tare_ok.drift = u.lc.meas   // the zero read when the check first failed
          u.conditions.tare_ok.ok = ok
          if (ok) { u.xv01.set(1); go("FAST") }   // hold in TARE with the condition shown
        }
        break
      case "FAST":
        if (u.lc.meas >= u.sp - u.dribbleBand) { u.xv01.set(0); u.xv02.set(1); go("DRIBBLE") }
        break
      case "DRIBBLE":
        if (u.lc.meas >= u.sp - u.preact) { u.xv02.set(0); go("SETTLE") }
        break
      case "SETTLE":
        if (u.phaseT >= C.SETTLE_S) go("VERIFY")
        break
      case "VERIFY": {
        const actual = u.lc.meas, err = actual - u.sp, t = tol()
        const result = Math.abs(err) <= t ? "ok" : err > 0 ? "overshoot" : "undershoot"
        emit("dose.verify", { unit: u.id, batchId: u.batchId, sp: u.sp, actual, err, result })
        // Adaptive dosing: the preact learns from every verify; only an out-of-tolerance one is announced as a trim.
        const from = u.preact
        // One verify moves the preact at most PREACT_MAX_STEP: a single outlier (a sticky stroke) must not be chased.
        const step = Math.max(-C.PREACT_MAX_STEP, Math.min(C.PREACT_MAX_STEP, C.PREACT_GAIN * err))
        u.preact = Math.min(C.PREACT_MAX_KG, Math.max(C.PREACT_MIN_KG, u.preact + step))
        if (result !== "ok") emit("preact.trim", { unit: u.id, from, to: u.preact, err })
        u.mtr.target = u.rpmTarget
        go("MIX_RAMP")
        break
      }
      case "MIX_RAMP":
        if (u.mtr.rpm >= u.rpmTarget - 1) go("MIX_HOLD")
        break
      case "MIX_HOLD":
        if (u.phaseT >= C.MIX_HOLD_S) { u.mtr.target = 0; go("MIX_COAST") }
        break
      case "MIX_COAST":
        if (u.mtr.rpm <= 0) go("WAIT_TRANSFER")
        break
      case "WAIT_TRANSFER":
        if (env.tankPermissive && !env.tankInterlock) { u.conditions.tank_hihi.firstOut = false; u.xv03.set(1); go("DRAIN") }
        break
      case "DRAIN":
        if (u.lc.true <= C.HEEL_KG) { u.xv03.set(0); go("EMPTY") }
        break
      case "EMPTY":
        if (u.xv03.state() === "closed") { u.lc.true = 0; go("IDLE") }
        break
    }
  }

  // Shock envelope: baseline grows with rpm and fill; seeded spikes during mixing; the PLC sees a 1 s average.
  function shock(dt, env) {
    const fill = Math.min(1, u.lc.true / 140)
    let g = 0.3 + 0.0004 * u.mtr.rpm + 0.25 * fill * (u.mtr.rpm > 0 ? 1 : 0)
    if (u.flowIn > 0) g += 0.15
    if (u.mtr.rpm > 0 && rng() < 0.02) g += 1.2 * rng()          // normal mixing transients
    if (u.faults.has("cavitation") && u.mtr.rpm > 0) g += 3.5 + 0.4 * Math.sin(env.t * 8)
    g += 0.02 * gauss(rng)
    u.shockPk = Math.max(0, g)
    if (u._plcN === 10) u._plcSum -= u._plcRing[u._plcI]; else u._plcN++
    u._plcRing[u._plcI] = u.shockPk; u._plcSum += u.shockPk; u._plcI = (u._plcI + 1) % 10
    u.plcRms = u._plcSum / u._plcN
    u.conditions.shock_ok.ok = u.shockPk < C.SHOCK_LIMIT_G
    if (!u.conditions.shock_ok.ok) u.conditions.shock_ok.firstOut = true
    if (u.faults.has("cavitation") && u.phase === "MIX_HOLD" && !u.hold) u.mtr.target = Math.round(u.rpmTarget * 0.78)
  }

  return u
}

const UNIT_SIGNALS = [
  ["wt", "Weight", "kg", "analog"], ["flow_in", "Feed", "kg/s", "analog"], ["flow_out", "Drain", "kg/s", "analog"],
  ["rpm", "Agitator", "rpm", "analog"], ["shock_pk", "Edge peak", "g", "analog"], ["plc_rms", "PLC scan", "g", "analog"],
  ["xv01", "Fast valve", "", "digital"], ["xv02", "Dribble valve", "", "digital"], ["xv03", "Outlet", "", "digital"],
  ["mtr_run", "Agitator run", "", "digital"], ["phase", "Phase", "", "digital"], ["hold", "Hold", "", "digital"],
  ["preact", "Preact", "kg", "analog"], ["sp", "Setpoint", "kg", "analog"],
  ["xv02_tclose", "Dribble close time", "s", "analog"],
]
export const SIGNALS = []
for (const d of STATION_DEFS) for (const [k, label, unit, kind] of UNIT_SIGNALS)
  SIGNALS.push({ key: `${d.id}.${k}`, label: `${d.id} ${label}`, unit, kind, group: d.id, color: d.color })
for (const [k, label, unit, kind] of [
  ["level_pct", "Level", "%", "analog"], ["mass", "Mass", "kg", "analog"], ["flow_in", "Inflow", "kg/s", "analog"],
  ["flow_out", "Dispense", "kg/s", "analog"], ["permissive", "Permissive", "", "digital"], ["interlock", "High-high", "", "digital"],
  ["line_run", "Line running", "", "digital"],
]) SIGNALS.push({ key: `TK-200.${k}`, label: `TK-200 ${label}`, unit, kind, group: "TK-200", color: "#f59e0b" })
SIGNALS.push({ key: "batch.id", label: "Batch", unit: "", kind: "digital", group: "batch", color: "#94a3b8" })
export const SIGNAL_INDEX = Object.fromEntries(SIGNALS.map((s, i) => [s.key, i]))

export function createModel({ seed = 1, recipe = "marinara" } = {}) {
  const rng = createRng(seed)
  const listeners = new Set()
  const model = {
    t: 0, units: [], recipeKey: recipe, faults: new Set(), events: [], alarms: new Map(),
    tank: { mass: 0.38 * C.TANK_CAP, levelPct: 38, flowIn: 0, flowOut: 0, permissive: true, interlock: false, lineRunning: true, dispensed: 0 },
    batches: { current: 1, completed: 0, startedAt: 0 },
    onEvent(fn) { listeners.add(fn); return () => listeners.delete(fn) },
    step, run(seconds) { const n = Math.round(seconds / C.DT); for (let i = 0; i < n; i++) step() },
    snapshot, setRecipe, setLineRunning(on) { if (on && model.faults.has("tank_high")) clearFault("tank_high"); else model.tank.lineRunning = !!on },
    injectFault, clearFault, clearFaults, restart, holdUnit(id) { unit(id).holdUnit() }, resumeUnit(id) { unit(id).resumeUnit() },
    ackAlarm,
  }
  const pendingVerify = []
  function emit(type, payload) {
    const e = { type, t: model.t, ...payload }
    if (type === "dose.verify") pendingVerify.push(e)   // annunciated by evaluateAlarms() at the end of this step
    model.events.push(e); if (model.events.length > 500) model.events.shift()
    for (const fn of listeners) fn(e)
  }
  function unit(id) { return model.units.find((u) => u.id === id) }

  for (const d of STATION_DEFS) {
    const u = createUnit(d, { rng, emit })
    u.totalDosedKg = 0
    model.units.push(u)
  }
  setRecipe(recipe)
  attachWear(model, emit, rng)
  const alarmDefs = buildAlarmDefs()
  for (const u of model.units) u.startBatch(1)
  emit("batch.start", { batchId: 1 })

  function setRecipe(key) {
    const r = RECIPES[key] || RECIPES.marinara
    model.recipeKey = key
    model.units.forEach((u, i) => u.setRecipe(r.targets[i], r.rpms[i]))
  }

  function injectFault(name) {
    if (model.faults.has(name)) return
    model.faults.add(name)
    emit("fault.injected", { name })
    if (name === "sticky_valve") {
      const u = unit("ST-102"); u.xv02.tClose = wearCloseTime(u.xv02) * 8
      // The fault lasts one dribble stroke: this batch's if the dribble close is still ahead, else the next batch's.
      u._stickyBatch = ["TARE", "FAST", "DRIBBLE"].includes(u.phase) ? u.batchId : u.batchId + 1
    }
    if (name === "cavitation") unit("ST-103").faults.add("cavitation")
    if (name === "loadcell_drift") unit("ST-105").lc.zeroOffset = 0.9
    if (name === "tank_high") model.tank.lineRunning = false
  }
  function clearFault(name) {
    model.faults.delete(name)
    emit("fault.cleared", { name })
    if (name === "sticky_valve") { const u = unit("ST-102"); u.xv02.tClose = wearCloseTime(u.xv02); u._stickyBatch = null }
    if (name === "cavitation") {
      const u = unit("ST-103"); u.faults.delete("cavitation"); u.conditions.shock_ok.firstOut = false
      if (!u.hold) u.mtr.target = u.phase === "MIX_HOLD" || u.phase === "MIX_RAMP" ? u.rpmTarget : 0
    }
    if (name === "loadcell_drift") unit("ST-105").lc.zeroOffset = 0
    if (name === "tank_high") model.tank.lineRunning = true
  }
  /** Restart the current batch on every unit: close everything, empty the vessels and the in-flight ring, reset the lead bookkeeping. */
  function restart() {
    clearFaults()
    for (const u of model.units) {
      u.xv01.set(0); u.xv02.set(0); u.xv03.set(0); u.mtr.target = 0
      u.hold = false; u.cmdSrc = "Prog"; u._pendingNext = null
      u.lc.true = 0; u.lc.settled = 0; u.lc.meas = 0
      while (u.inflight.total() > 0) u.inflight.pop()
      u._emptiedBatch = model.batches.current - 1
    }
    for (const u of model.units) u.startBatch(model.batches.current)
    emit("batch.start", { batchId: model.batches.current })
    resetAlarms()
  }

  function clearFaults() { for (const f of Array.from(model.faults)) clearFault(f) }

  function step() {
    const dt = C.DT
    model.t += dt
    const tk = model.tank
    tk.permissive = tk.levelPct < C.TANK_PERMISSIVE_PCT
    const trippedNow = tk.levelPct >= C.TANK_HIHI_PCT
    if (trippedNow !== tk.interlock) { tk.interlock = trippedNow; emit("tank.interlock", { tripped: trippedNow }) }

    // Shared pump: scale every open outlet so the manifold never exceeds PUMP_CAP.
    let want = 0
    for (const u of model.units) {
      const v = u.xv03
      const p = v.cmd > v.pos ? Math.min(v.cmd, v.pos + dt / v.tOpen) : Math.max(v.cmd, v.pos - dt / v.tClose)
      if (p > 0) want += C.Q_DRAIN * p
    }
    const drainScale = want > C.PUMP_CAP ? C.PUMP_CAP / want : 1

    const env = { tankPermissive: tk.permissive, tankInterlock: tk.interlock, drainScale, t: model.t }
    let inflow = 0
    let anyWaitingHeld = false
    for (const u of model.units) tryStart(u)
    for (const u of model.units) {
      const before = u.phase
      u.step(dt, env)
      u.totalDosedKg += u.flowIn * dt
      if (u.xv01.pos > 0) u.xv01.runH += dt / 3600
      if (u.xv02.pos > 0) u.xv02.runH += dt / 3600
      if (u.xv03.pos > 0) u.xv03.runH += dt / 3600
      if (u.mtr.rpm > 0) u.mtr.runH += dt / 3600
      inflow += u.flowOut
      if (u.phase === "WAIT_TRANSFER" && !tk.permissive) anyWaitingHeld = true
      if (before !== "IDLE" && u.phase === "IDLE") onUnitEmpty(u)
      if (u._stickyBatch != null && u.batchId > u._stickyBatch) { u._stickyBatch = null; if (model.faults.has("sticky_valve")) clearFault("sticky_valve") }
    }
    if (anyWaitingHeld !== model._held) { model._held = anyWaitingHeld; emit("tank.permissive", { held: anyWaitingHeld, levelPct: tk.levelPct }) }

    const canDispense = tk.lineRunning && tk.levelPct > C.TANK_LOLO_PCT
    const out = canDispense ? Math.min(C.LINE_DRAW, tk.mass / dt) : 0
    tk.flowIn = inflow; tk.flowOut = out
    tk.mass += (inflow - out) * dt
    tk.dispensed += out * dt
    tk.levelPct = (100 * tk.mass) / C.TANK_CAP
    evaluateAlarms()
  }

  // ---- Alarms: conditions observed once per step, annunciated as events (the EventDetect layer). ----
  // A condition must hold ALARM_RAISE_S before alarm.raised and stay clear ALARM_CLEAR_S before alarm.cleared, so
  // nothing flaps. A changed text (a count, a level) is alarm.updated, never a second raise. Delays are counted in
  // steps, not summed seconds, so they are exact. This runs at 10 Hz through the whole 8 h backfill, so the
  // conditions are read in one monomorphic pass into `alarmAct` (same order as `alarmDefs`), each definition caches
  // its live record, the per-definition loop is skipped while nothing is active or pending (most steps), and an
  // active alarm's text is refreshed once a second rather than every step.
  const RAISE_N = Math.round(C.ALARM_RAISE_S / C.DT), CLEAR_N = Math.round(C.ALARM_CLEAR_S / C.DT)
  function buildAlarmDefs() {
    const tk = model.tank
    const pct = () => Math.round(tk.levelPct)
    const def = (id, severity, unitId, text) => ({ id, severity, unit: unitId, text, on: 0, off: 0, rec: null, shockOf: null, peak: 0 })
    const defs = [   // ORDER MATTERS: evaluateAlarms() fills alarmAct in exactly this order
      def("line.stopped", "warning", null, () => "Filling line stopped — TK-200 filling, transfers hold at 72 %"),
      def("tank.permissive_held", "warning", null, () => {
        const n = model.units.reduce((k, u) => k + (u.phase === "WAIT_TRANSFER" ? 1 : 0), 0)
        return `TK-200 at ${pct()} % — transfers held, ${n} station${n === 1 ? "" : "s"} waiting`
      }),
      def("tank.hihi", "alarm", null, () => `TK-200 high-high trip at ${pct()} % — outlets closed`),
      def("tank.lolo", "warning", null, () => "TK-200 low — filling line starved"),
    ]
    for (const u of model.units) {
      // The shock text carries the episode's peak (to 0.1 g), so it updates on a new peak, not on every sample.
      const shock = def(`unit.${u.id}.shock`, "alarm", u.id, () => `${u.id} shock envelope ${shock.peak.toFixed(1)} g above ${C.SHOCK_LIMIT_G} g limit`)
      shock.shockOf = u
      defs.push(shock,
        def(`unit.${u.id}.tare`, "warning", u.id, () => `${u.id} tare check failed — zero drift ${(u.conditions.tare_ok.drift ?? 0).toFixed(1)} kg`),
        def(`unit.${u.id}.wear`, "warning", u.id, () => `${u.id} dribble valve rebuild due in ${u.xv02.rebuildDueBatches} batches`),
        def(`unit.${u.id}.hold`, "warning", u.id, () => `${u.id} held by operator`))
    }
    return defs
  }
  const alarmAct = new Uint8Array(4 + 4 * model.units.length)
  let alarmLive = 0     // definitions with a running raise/clear timer or a live record
  let alarmTick = 0
  function raiseAlarm(id, severity, unitId, text) {
    const rec = { id, severity, text, unit: unitId, raisedAt: model.t, acked: false }
    model.alarms.set(id, rec)
    emit("alarm.raised", { id, severity, text, unit: unitId })
    return rec
  }
  function updateAlarm(rec, text) {
    if (rec.text === text) return
    rec.text = text
    emit("alarm.updated", { id: rec.id, text })
  }
  function clearAlarm(id) {
    if (!model.alarms.delete(id)) return
    emit("alarm.cleared", { id })
  }
  function evaluateAlarms() {
    const tk = model.tank, act = alarmAct
    let waiting = false
    for (const u of model.units) if (u.phase === "WAIT_TRANSFER") { waiting = true; break }
    let any = 0
    any |= act[0] = tk.lineRunning ? 0 : 1
    any |= act[1] = !tk.permissive && waiting ? 1 : 0
    any |= act[2] = tk.interlock ? 1 : 0
    any |= act[3] = tk.lineRunning && tk.levelPct <= C.TANK_LOLO_PCT ? 1 : 0
    let k = 4
    for (const u of model.units) {
      const due = u.xv02.rebuildDueBatches
      any |= act[k++] = u.conditions.shock_ok.ok ? 0 : 1
      any |= act[k++] = u.conditions.tare_ok.ok ? 0 : 1
      any |= act[k++] = due != null && due <= C.WEAR_DUE_BATCHES ? 1 : 0
      any |= act[k++] = u.hold ? 1 : 0
    }
    if (any || alarmLive) {
      const refresh = ++alarmTick >= RAISE_N
      if (refresh) alarmTick = 0
      let live = 0
      for (let i = 0; i < alarmDefs.length; i++) {
        const d = alarmDefs[i]
        if (act[i]) {
          d.off = 0
          if (d.shockOf) { if (!d.rec && d.on === 0) d.peak = 0; if (d.shockOf.shockPk > d.peak) d.peak = d.shockOf.shockPk }
          if (d.rec) { if (refresh) updateAlarm(d.rec, d.text()) }
          else if (++d.on >= RAISE_N) { d.on = 0; d.rec = raiseAlarm(d.id, d.severity, d.unit, d.text()) }
        } else if (d.rec) {
          if (++d.off >= CLEAR_N) { d.off = 0; d.rec = null; clearAlarm(d.id) }
        } else d.on = 0
        if (d.on || d.rec) live++
      }
      alarmLive = live
    }
    // A dose verify is itself the event: out of tolerance raises (or updates) at once, the unit's next good dose clears.
    for (const e of pendingVerify) {
      const id = `unit.${e.unit}.verify`, rec = model.alarms.get(id)
      if (e.result === "ok") { clearAlarm(id); continue }
      const text = `${e.unit} dose ${e.err >= 0 ? "+" : "-"}${Math.abs(e.err).toFixed(2)} kg outside tolerance`
      if (rec) updateAlarm(rec, text); else raiseAlarm(id, "warning", e.unit, text)
    }
    pendingVerify.length = 0
  }
  function ackAlarm(id) {
    const rec = model.alarms.get(id)
    if (!rec || rec.acked) return
    rec.acked = true
    emit("alarm.acked", { id })
  }
  /** Restart: every active alarm is cleared (emitted) and every hysteresis timer starts over. */
  function resetAlarms() {
    for (const id of Array.from(model.alarms.keys())) clearAlarm(id)
    for (const d of alarmDefs) { d.on = 0; d.off = 0; d.rec = null; d.peak = 0 }
    alarmLive = 0; alarmTick = 0
    pendingVerify.length = 0
  }

  function onUnitEmpty(u) {
    const id = u.batchId
    u._emptiedBatch = id
    if (model.units.every((x) => (x._emptiedBatch ?? 0) >= id)) {
      model.batches.completed += 1
      emit("batch.complete", { batchId: id, kg: model.units.reduce((s, x) => s + x.sp, 0) })
    }
    u._pendingNext = id + 1
    tryStart(u)
  }

  /** A unit may start batch N+1 only once every unit has emptied batch N-1: nobody runs more than one batch ahead. */
  function tryStart(u) {
    if (u.phase !== "IDLE" || u._pendingNext == null) return
    const next = u._pendingNext
    if (!model.units.every((x) => (x._emptiedBatch ?? 0) >= next - 2)) return
    u._pendingNext = null
    if (next > model.batches.current) {
      model.batches.current = next
      model.batches.startedAt = model.t
      emit("batch.start", { batchId: next })
    }
    u.startBatch(next)
  }

  /** Returns a reused buffer: callers (history, UI) must copy what they keep. */
  function snapshot() {
    const out = model._snap || (model._snap = new Float64Array(SIGNALS.length))
    let i = 0
    for (const u of model.units) {
      out[i++] = u.lc.meas; out[i++] = u.flowIn; out[i++] = u.flowOut; out[i++] = u.mtr.rpm
      out[i++] = u.shockPk; out[i++] = u.plcRms
      out[i++] = u.xv01.pos > 0 ? 1 : 0; out[i++] = u.xv02.pos > 0 ? 1 : 0; out[i++] = u.xv03.pos > 0 ? 1 : 0
      out[i++] = u.mtr.rpm > 0 ? 1 : 0; out[i++] = PHASES.indexOf(u.phase); out[i++] = u.hold ? 1 : 0
      out[i++] = u.preact; out[i++] = u.sp; out[i++] = u.xv02.tClose
    }
    const tk = model.tank
    out[i++] = tk.levelPct; out[i++] = tk.mass; out[i++] = tk.flowIn; out[i++] = tk.flowOut
    out[i++] = tk.permissive ? 1 : 0; out[i++] = tk.interlock ? 1 : 0; out[i++] = tk.lineRunning ? 1 : 0
    out[i++] = model.batches.current
    return out
  }
  return model
}

export const WEAR_SEED_STROKES = [2200, 4100, 6800, 10865, 1500, 3300]

/** Quadratic seal wear plus a failure knee past life. Life = the stroke count where the preact clamp is reached (tClose = tCloseNew * (1 + WEAR_FACTOR_AT_LIFE)). */
export function wearCloseTime(valve) {
  const x = valve.strokes / C.WEAR_LIFE_STROKES
  return valve.tCloseNew * (1 + C.WEAR_FACTOR_AT_LIFE * x * x) +
    C.WEAR_KNEE_S_PER_STROKE * Math.max(0, valve.strokes - C.WEAR_LIFE_STROKES)
}

function median(xs) {
  const s = xs.slice().sort((a, b) => a - b), m = s.length >> 1
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

/** Least-squares slope of the measured close time per batch; projects batches until close time reaches life (tCloseNew * (1 + WEAR_FACTOR_AT_LIFE)). */
function projectDue(valve) {
  const h = valve.closeTimeHistory
  if (h.length < 8) return null
  const n = h.length
  let sx = 0, sy = 0, sxx = 0, sxy = 0
  for (const p of h) { sx += p.batchId; sy += p.tClose; sxx += p.batchId * p.batchId; sxy += p.batchId * p.tClose }
  const slope = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1)
  if (slope <= 1e-7) return null
  const limit = valve.tCloseNew * (1 + C.WEAR_FACTOR_AT_LIFE)
  return Math.max(0, Math.ceil((limit - h[n - 1].tClose) / slope))
}

/** Run a scratch unit with a given close time for 8 batches and return the preact its trim loop settles at. */
function calibratePreact(def, rng, tClose, sp, rpm) {
  const sc = createUnit(def, { rng: createRng(Math.floor(rng() * 2 ** 31)), emit: () => {} })
  sc.xv02.tClose = tClose
  sc.setRecipe(sp, rpm)
  const env = { tankPermissive: true, tankInterlock: false, drainScale: 1, t: 0 }
  for (let k = 1; k <= 8; k++) {
    sc.startBatch(k)
    for (let n = 0; n < 4000 && sc.phase !== "IDLE"; n++) { env.t += C.DT; sc.step(C.DT, env) }
  }
  return sc.preact
}

function attachWear(model, emit, rng) {
  model.units.forEach((u, i) => {
    const def = STATION_DEFS[i]
    u.xv02.strokes = WEAR_SEED_STROKES[i]
    u.xv02.tClose = wearCloseTime(u.xv02)
    // A long-worn valve has a learned preact: calibrate it on a scratch unit rather than assume a formula.
    u.preact = calibratePreact(def, rng, u.xv02.tClose, u.sp, u.rpmTarget)
    u.xv02.closeTimeHistory = []
    u.xv02.rebuildDueBatches = null
    u.xv02._dueAnnounced = false
    u.xv01.runH = 0; u.xv02.runH = 0; u.xv03.runH = 0; u.mtr.runH = 0
  })
  model.onEvent((e) => {
    if (e.type !== "dose.verify") return
    const u = model.units.find((x) => x.id === e.unit)
    const v = u.xv02
    if (!model.faults.has("sticky_valve") || u.id !== "ST-102") v.tClose = wearCloseTime(v)
    // The edge sensor times every close stroke; that drift is the wear signal (the learned preact hides it).
    // A stroke more than twice the typical one is a stuck valve, not wear: it shows as a fault and an overshoot, and
    // must not enter the regression (one sticky stroke would otherwise project the rebuild as due now, forever).
    const h = v.closeTimeHistory
    const typical = h.length ? median(h.map((p) => p.tClose)) : wearCloseTime(v)
    if (v.tClose <= 2 * typical) {
      h.push({ batchId: e.batchId, tClose: v.tClose })
      if (h.length > 60) h.shift()
    }
    v.rebuildDueBatches = projectDue(v)
    if (v.rebuildDueBatches != null && v.rebuildDueBatches <= C.WEAR_DUE_BATCHES && !v._dueAnnounced) {
      v._dueAnnounced = true
      emit("wear.rebuild_due", { unit: u.id, dueInBatches: v.rebuildDueBatches })
    }
  })
  model.rebuild = (id) => {
    const u = model.units.find((x) => x.id === id)
    const v = u.xv02
    v.strokes = 0; v.tClose = v.tCloseNew; v.closeTimeHistory = []; v.rebuildDueBatches = null; v._dueAnnounced = false
    u.preact = STATION_DEFS.find((d) => d.id === id).preact
    emit("wear.rebuilt", { unit: id })
  }
  model.backfill = (seconds, onSample) => { for (const _ of model.backfillSlices(seconds, onSample, Infinity)) { /* one slice */ } }
  /**
   * The same backfill in slices of `slice` steps, yielding the fraction done after each one, so a page can paint its
   * loading progress between slices (main.js boot). Run to the end it is exactly backfill(): same steps, t ends at 0.
   */
  model.backfillSlices = function* (seconds, onSample, slice = 3000) {
    const n = Math.round(seconds / C.DT)
    model.t = -seconds
    for (let i = 0; i < n; i++) {
      model.step(); onSample(model.t, model.snapshot())
      if ((i + 1) % slice === 0 && i + 1 < n) yield (i + 1) / n
    }
    model.t = 0
    yield 1
  }
}
